import { useStore } from '@nanostores/react';
import { useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { cssTransition, ToastContainer } from 'react-toastify';
import { themeStore } from '~/lib/stores/theme';
import { logStore } from '~/lib/stores/logs';
import { $showAccountSettings, $accountSettingsTab, closeAccountSettings } from '~/lib/stores/accountSettings';
import AuthProvider from '~/components/auth/AuthProvider';
import PostHogProvider from '~/components/providers/PostHogProvider';
import ActiveGenerationProvider from '~/components/providers/ActiveGenerationProvider';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { AccountSettingsModal } from '~/components/account/AccountSettingsModal';
import AuthModal from '~/components/auth/AuthModal';
import AppRoutes from '~/routes';
import { BREAKPOINTS, useIsBelow } from '~/lib/hooks/useMediaQuery';
import { useVisualViewportHeight } from '~/lib/hooks/useVisualViewportHeight';

const toastAnimation = cssTransition({
  enter: 'animated fadeInRight',
  exit: 'animated fadeOutRight',
});

export default function App() {
  const theme = useStore(themeStore);
  const showAccountSettings = useStore($showAccountSettings);
  const accountSettingsTab = useStore($accountSettingsTab);
  // On phones a bottom toast sits on top of the chat composer and Send button.
  const isPhone = useIsBelow(BREAKPOINTS.sm);
  useVisualViewportHeight();

  useEffect(() => {
    document.querySelector('html')?.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    // Attribution and the landing-prompt handoff moved to main.tsx: from here
    // they ran after ChatImpl had already read an empty draft. See main.tsx.
    logStore.logSystem('Application initialized', {
      theme,
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });

    import('~/utils/debugLogger')
      .then(({ debugLogger }) => {
        const status = debugLogger.getStatus();
        logStore.logSystem('Debug logging ready', {
          initialized: status.initialized,
          capturing: status.capturing,
          enabled: status.enabled,
        });
      })
      .catch((error) => {
        logStore.logError('Failed to initialize debug logging', error);
      });
  }, []);

  return (
    <>
      <ErrorBoundary>
        <DndProvider backend={HTML5Backend}>
          <AuthProvider>
            <PostHogProvider>
              <ActiveGenerationProvider />
              <AppRoutes />
            </PostHogProvider>
          </AuthProvider>
        </DndProvider>
      </ErrorBoundary>
      <AuthModal />
      <AccountSettingsModal open={showAccountSettings} onClose={closeAccountSettings} initialTab={accountSettingsTab} />
      <ToastContainer
        closeButton={({ closeToast }) => {
          return (
            <button className="Toastify__close-button" onClick={closeToast}>
              <div className="i-ph:x text-lg" />
            </button>
          );
        }}
        icon={({ type }) => {
          // Glyph only — the icon badge's tint/color comes from toast.scss per variant.
          switch (type) {
            case 'success': {
              return <div className="i-ph:check-bold" />;
            }
            case 'error': {
              return <div className="i-ph:x-bold" />;
            }
            case 'warning': {
              return <div className="i-ph:warning-bold" />;
            }
            case 'info': {
              return <div className="i-ph:info-bold" />;
            }
          }

          return undefined;
        }}
        position={isPhone ? 'top-center' : 'bottom-right'}
        pauseOnFocusLoss
        transition={toastAnimation}
        autoClose={3000}
      />
    </>
  );
}
